import React, { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  Switch,
  RouteProps,
  RouteComponentProps,
  NavLink,
} from "react-router-dom";

import "./index.scss";
import Home from "src/scenes/Home";
import ImComHome from "src/scenes/Imcom/Home";
import India2MISHome from "src/scenes/India2MIS/Home";
import ImComDetail from "src/scenes/Imcom/Details";
import India2MISDetail from "src/scenes/India2MIS/Details";
import ImComThirdScreen from "src/scenes/Imcom/ThirdScreen";
import India2MISThirdScreen from "src/scenes/India2MIS/ThirdScreen";
import ImComFourthScree from "src/scenes/Imcom/FourthScreen";
// import India2MISFourthScree from "src/scenes/India2MIS/FourthScreen";
import Category from "src/scenes/Category";
import DetailsComponent from "src/scenes/Details";
import TakeNotes from "src/scenes/TakeNotes";
import Login from "src/scenes/Login";
import OAuthCallback from "src/scenes/Login/OAuthCallback";
import Search from "src/shared/components/Search";
import ProtectedRoute from "./components/ProtectedRoute";
import OfflineComp from "./components/Offline";
import userService from "src/shared/services/user.service";
import useJoyride, { JoyrideProvider } from "./useJoyride";
import OrientationService from "./orientation.service";
import SnackbarComponent from "./components/Snackbar";
import OrientationEncapsulator from "./components/OrientationEncapsulator";
import OnlineStatus from "src/shared/components/OnlineStatus";
import MetricComparison from "src/scenes/MetricComparison";
import { useInterval, useRedux } from "src/shared/hooks";
import { IRootState, NotificationType } from "src/shared/types";
import { Button, Spin } from "antd";
import styled from "@emotion/styled";
import { fetchAppConfig, fetchUserRoles, TIME_TAB_RESET, fetchNotification } from "src/store/app";
import { logoutAction } from "src/store/login";
import TopNavbar from "src/shared/components/Navbar/NewNavbar";
import { setNotification } from "src/shared/utils/alert";
import Watermark from "src/shared/components/Watermark";
import { BANNER_API_REFRESH_INTERVAL } from "src/shared/config";
import AnobisLogo from "src/assets/anobis-logo.svg";
import ErrorBoundary from "src/shared/components/ErrorBoundary";

const Spinner = styled.article`
  padding: 50%;
`;

const ErrorMsg = styled.article`
  border: 1px dashed white;
  border-radius: 5px;
  color: white;
  opacity: 0.5;
  text-align: center;
  padding: 15%;
  margin: 15vh 10px;
`;

function PurgeCache() {
  const [isDone, setIsDone] = useState(false);
  useEffect(() => {
    (async () => {
      indexedDB.deleteDatabase("workbox-expiration");
      await caches.keys().then((cs) => Promise.all(cs.map((c) => caches.delete(c))));
      await Promise.all((await navigator.serviceWorker.getRegistrations()).map((w) => w.unregister()));
      setIsDone(true);
    })();
  }, []);
  return <div style={{ color: "white" }}>{isDone ? "DONE" : "DOING..."}</div>;
}

function MockDelay({ location }: RouteProps) {
  const [status, setStatus] = useState("INIT");
  useEffect(() => {
    setStatus("PENDING");
    fetch(`/api/darkroom/mock-delay${location ? location.search : "?value=0"}`)
      .then(() => setStatus("DONE"))
      .catch(() => setStatus("ERROR"));
  }, []);
  return <div style={{ color: "white" }}>{status}</div>;
}

function DarkRoom() {
  return (
    <Route
      component={({ match }) => (
        <div>
          <Route path={`${match.path}/purgecache`} component={PurgeCache} />
          <Route path={`${match.path}/mock-delay`} component={MockDelay} />
        </div>
      )}
    />
  );
}

function Fallback({ logoutAction, history, message }) {
  const onLogout = useCallback(() => {
    userService.logout();
    history.push("/");
    logoutAction();
  }, []);
  return (
    <>
      <TopNavbar isHome handleClick={() => {}} onLogout={onLogout} />
      <ErrorMsg>
        <p>{message}</p>
        <Button size="small" onClick={() => (window.location.href = window.location.href)}>
          Refresh
        </Button>
      </ErrorMsg>
    </>
  );
}

function mapStateToProps(state: IRootState) {
  return {
    appConfig: state.app.appConfig,
    userRoles: state.app.userRoles,
    timerange: state.app.selectedTab,
    timerangeConfig: state.app.appConfig.payload,
    notification: state.app.notification,
  };
}

const actionsToPropsMap = {
  fetchAppConfig,
  fetchUserRoles,
  logoutAction,
  fetchNotification,
};

const dashboardOverlayHiddenConfig = {
  visibility: false,
  overlayMessage: "",
};

const dashboardOverlayErrorConfig = {
  visibility: true,
  overlayMessage: `<span>
  <b>Something looks wrong, please refresh screen.</b> </br>
  For any issue, query or feedback Please reach out to us at <a target="_blank" rel="noreferrer" href="https://swiggy.slack.com/archives/anobis-support"> #anobis-support </a>or<a href="mailto:data.products@swiggy.in? &amp;subject=Anobis related issue"> data.products@swiggy.in</a>
  </span>`,
};

function Dashboard({ history }: RouteComponentProps) {
  const joyrideValue = useJoyride();
  const [isPotraitMode, setIsPotraitMode] = useState(OrientationService.checkIfPortraitMode());
  const [{ siteHeight, siteWidth }, setDimensions] = useState(OrientationService.getDimensions());
  const [dashboardOverlayConfig, setDashboardOverlayConfig] = useState(dashboardOverlayHiddenConfig);

  const mappedProps = useRedux<IRootState, ReturnType<typeof mapStateToProps>, typeof actionsToPropsMap, {}>(
    mapStateToProps,
    actionsToPropsMap
  );

  useEffect(() => {
    if (!mappedProps.appConfig.success && !mappedProps.userRoles.pending) {
      mappedProps.fetchAppConfig();
    }
    if (!mappedProps.userRoles.success && !mappedProps.userRoles.pending) {
      mappedProps.fetchUserRoles();
    }
  }, []);

  useEffect(() => {
    /**Feature to reset the time tab on session close*/
    if (!sessionStorage.getItem(TIME_TAB_RESET)) sessionStorage.setItem(TIME_TAB_RESET, TIME_TAB_RESET);
    OrientationService.addOrientationChangeEventListener((isPotraitModeArg: boolean) => {
      setIsPotraitMode(isPotraitModeArg);
      setDimensions(OrientationService.getDimensions());
    });
    OrientationService.init();
    mappedProps.fetchNotification();
    return () => {
      OrientationService.removeOrientationChangeEventListener();
    };
  }, []);

  useInterval(() => {
    mappedProps.fetchNotification();
  }, BANNER_API_REFRESH_INTERVAL * 1000);

  useEffect(() => {
    if (mappedProps.notification.errors) {
      setDashboardOverlayConfig(dashboardOverlayErrorConfig);
      return;
    }
    const { isVisible, message, alert } = mappedProps.notification.payload || {};
    if (isVisible) {
      if (alert.toLowerCase() === NotificationType.INFO) {
        setDashboardOverlayConfig({
          visibility: true,
          overlayMessage: message,
        });
      } else {
        setDashboardOverlayConfig(dashboardOverlayHiddenConfig);
        setNotification(alert, message);
      }
    } else {
      setDashboardOverlayConfig(dashboardOverlayHiddenConfig);
    }
  }, [mappedProps.notification]);

  if (dashboardOverlayConfig.visibility) {
    return (
      <div className="dashboard-overlay">
        <div className="logo">
          <img src={AnobisLogo} alt="Anobis Logo" />
          <span>Anobis</span>
        </div>
        <div className="overlay-content" dangerouslySetInnerHTML={{ __html: dashboardOverlayConfig.overlayMessage }} />
      </div>
    );
  }

  return (
    <OrientationEncapsulator
      isPortraitMode={isPotraitMode}
      siteHeight={siteHeight}
      siteWidth={siteWidth}
    >
      <JoyrideProvider value={joyrideValue}>
        <React.Suspense fallback={<Spinner><Spin /></Spinner>}>
          <ErrorBoundary
            fallback={
              <Fallback
                message="An unexpected error occurred!"
                history={history}
                logoutAction={mappedProps.logoutAction}
              />
            }
          >
            {mappedProps.appConfig.pending || mappedProps.userRoles.pending ? (
              <Spinner>
                <Spin />
              </Spinner>
            ) : mappedProps.appConfig.success && mappedProps.userRoles.success ? (
              <>
                <Watermark />
                <Route exact path="/" component={Home} />
                <Route exact path="/im-com-app" component={ImComHome} />
                <Route exact path="/india-2-app" component={India2MISHome} />
                <Route path="/category/:name" component={Category} />
                <Route path="/details/:type/:id" component={DetailsComponent} />
                <Route
                  exact
                  path="/im-com-app/details/:type/:id"
                  component={ImComDetail}
                />
                <Route
                  exact
                  path="/india-2-app/details/:type/:id"
                  component={India2MISDetail}
                />
                <Route
                  exact
                  path="/im-com-app/third-screen"
                  component={ImComThirdScreen}
                />
                <Route
                  exact
                  path="/india-2-app/third-screen"
                  component={India2MISThirdScreen}
                />
                <Route
                  exact
                  path="/im-com-app/details/fourth-screen/:type/:storeId/:id"
                  component={ImComFourthScree}
                />
                {/* <Route exact path="/india-2-app/details/fourth-screen/:type/:storeId/:id" component={India2MISFourthScree} /> */}
                <Route path="/:pinID/takeNotes" component={TakeNotes} />
                <Route path="/search" component={Search} />
                <Route path="/metric-comparison" component={MetricComparison} />
                <Route path="/darkroom" component={DarkRoom} />
              </>
            ) : mappedProps.appConfig.failure || mappedProps.userRoles.failure ? (
              <Fallback
                message="Couldn't fetch data!"
                history={history}
                logoutAction={mappedProps.logoutAction}
              />
            ) : null}
          </ErrorBoundary>
        </React.Suspense>
        {/* withConfig(): HOC: Makes a call to fetch app level configs (if not already fetched) before rendering a page */}
      </JoyrideProvider>
      <SnackbarComponent />
    </OrientationEncapsulator>
  );
}

function App() {
  return (
    <>
      <Router>
        <Switch>
          <Route
            path="/login"
            render={(props) => (userService.isLoggedIn() ? <Redirect to="/" /> : <Login {...props} />)}
          />
          <Route
            path="/im-com-app/login"
            render={(props) => (userService.isLoggedIn() ? <Redirect to="/im-com-app" /> : <Login {...props} />)}
          />
          <Route
            path="/india-2-app/login"
            render={(props) => (userService.isLoggedIn() ? <Redirect to="/india-2-app" /> : <Login {...props} />)}
          />
          <Route path="/offline" component={OfflineComp} />
          <Route path="/oauth_callback" component={OAuthCallback} />
          {/* ProtectedRoute: Redirects to login page if user is not logged in */}
          <ProtectedRoute component={Dashboard} path="" />
        </Switch>
      </Router>
      <OnlineStatus />
    </>
  );
}

export default App;
